'use client';

import { useEffect, useRef, type FC } from 'react';

import type { SanityPolicyEngineMetric } from '#/lib/sanity/queries/policyEngineAnimation';
import { cn } from '#/lib/utils';

const ELECTRIC_RGB = '216,251,75';
const CIPHER_RGB = '95,91,247';

// Dot beam — proportions from the Figma frame (1440w: band 94→1375, 67px tall)
const ROW_COUNT = 10;
const ROW_GAP = 7;
const DOT_PITCH = 7;
const DOT_R = 1.6;
const BELT_MS = 16875;
const FADE_FRAC = 0.22;
const RIGHT_PAD = 60;

// Decorative scatter — Figma (1440 frame) has ~6 intake clusters from
// y≈389–614 around a band at y≈481–548, i.e. arcs from ~90–125px above
// the band centre and ~70–100px below, concentrated at x≈140–290.
const SCATTER_COUNT = 120;
const SCATTER_COUNT_MOBILE = 52;
const SCATTER_FALL_MS = 2800;
const SCATTER_PERIOD_MS = 5600;
const SCATTER_SETTLE_MS = 380;
// Mobile canvas is shorter — arcs taller than the first pass, still below desktop
const SCATTER_Y_SCALE_MOBILE = 0.6;
// Cluster anchors from Figma 1440 frame, relative to band-start (x) and
// band-centre (y). Y is in canvas px (not width-scaled) so arcs stay tall.
const SCATTER_CLUSTERS = [
  { x: 46, y: -78, spreadX: 30, spreadY: 28 },
  { x: 78, y: -130, spreadX: 34, spreadY: 32 },
  { x: 63, y: -98, spreadX: 30, spreadY: 28 },
  { x: 90, y: 22, spreadX: 38, spreadY: 36 },
  { x: 100, y: 78, spreadX: 36, spreadY: 32 },
  { x: 130, y: 12, spreadX: 42, spreadY: 38 },
] as const;

const AUTO_ADVANCE_MS = 3200;
// Long enough that several callouts coexist (desktop has 4 slots; each new
// one lands before the oldest expires). Mobile uses a shorter hold below.
const CALLOUT_HOLD_MS = 11000;
const CALLOUT_HOLD_MS_MOBILE = 5600;
// Short pause before the first stat — still avoids an opening burst
const INITIAL_CALLOUT_PAUSE_MS = 900;
// Far-left scatter mix — ~30% Cipher purple per design feedback
const SCATTER_PURPLE_CHANCE = 0.3;
const MOBILE_BREAKPOINT = 700;

type Props = {
  metrics: SanityPolicyEngineMetric[];
  className?: string;
};

function rand(a: number, b: number) {
  return a + Math.random() * (b - a);
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function clamp(v: number, a: number, b: number) {
  return Math.max(a, Math.min(b, v));
}

function easeOutCubic(x: number) {
  return 1 - Math.pow(1 - x, 3);
}

function easeInCubic(x: number) {
  return x * x * x;
}

function shuffledIndices(n: number) {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export const PolicyEngineCanvas: FC<Props> = ({ metrics, className }) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Leaders sit under the canvas; labels sit above so wrapped text never
  // paints under the dots.
  const leadersRef = useRef<HTMLDivElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);
  const metricsRef = useRef(metrics);
  metricsRef.current = metrics;

  useEffect(() => {
    const stageNode = stageRef.current;
    const canvasNode = canvasRef.current;
    const leadersNode = leadersRef.current;
    const labelsNode = labelsRef.current;
    const contextNode = canvasNode?.getContext('2d');
    if (
      !stageNode ||
      !canvasNode ||
      !leadersNode ||
      !labelsNode ||
      !contextNode
    ) {
      return;
    }

    // Non-null aliases so nested animation closures typecheck under
    // strictNullChecks (narrowing doesn't survive into inner functions).
    const stage: HTMLDivElement = stageNode;
    const canvas: HTMLCanvasElement = canvasNode;
    const leadersEl: HTMLDivElement = leadersNode;
    const labelsEl: HTMLDivElement = labelsNode;
    const ctx: CanvasRenderingContext2D = contextNode;

    let W = 0;
    let H = 0;
    let DPR = 1;
    let horizonY = 0;
    let marginLeft = 0;
    let marginRight = 0;
    let isMobile = false;
    let startTime: number | null = null;
    let rafId = 0;
    let destroyed = false;
    let isVisible = true;
    let isPageVisible = !document.hidden;
    let nextMetricCursor = 0;
    let slotCursor = 0;
    let lastClickAt = 0;

    type Dot = { phaseFrac: number; targetY: number; alphaJitter: number };
    type ScatterDot = {
      cluster: number;
      // Start position for this cycle (high above / below the band)
      startX: number;
      startY: number;
      landX: number;
      landingY: number;
      phase0: number;
      entryTime: number | null;
      period: number;
      // Figma mixes Cipher purple + Electric lime in the intake
      rgb: typeof CIPHER_RGB | typeof ELECTRIC_RGB;
      falling: boolean;
      x: number;
      y: number;
      alpha: number;
    };
    type Slot = {
      metricIndex: number;
      frac: number;
      side: 'up' | 'down';
      leaderLen: number;
      hideTimer: number;
      visible: boolean;
      leaderRoot: HTMLDivElement;
      labelRoot: HTMLDivElement;
      line: HTMLDivElement;
      tip: HTMLDivElement;
      card: HTMLDivElement;
      value: HTMLDivElement;
      label: HTMLDivElement;
    };

    let dots: Dot[] = [];
    let scatterDots: ScatterDot[] = [];
    let slots: Slot[] = [];

    const bandHalf = ((ROW_COUNT - 1) / 2) * ROW_GAP;

    function beamStart() {
      return marginLeft;
    }

    function beamEnd() {
      return W - marginRight;
    }

    function beamWidth() {
      return beamEnd() - beamStart();
    }

    function beltWidth() {
      return beamWidth() + RIGHT_PAD;
    }

    function rowYs() {
      const mid = (ROW_COUNT - 1) / 2;
      return Array.from(
        { length: ROW_COUNT },
        (_, i) => horizonY + (i - mid) * ROW_GAP,
      );
    }

    function resize() {
      DPR = Math.min(window.devicePixelRatio || 1, 2);
      W = canvas.clientWidth;
      H = canvas.clientHeight;
      canvas.width = W * DPR;
      canvas.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      // Keep the band mid-canvas so Figma-height intake arcs (~±125px) fit
      horizonY = H * 0.52;
      isMobile = W < MOBILE_BREAKPOINT;
      // Figma: band runs 6.5% → 95.5% on desktop, full-bleed on mobile
      marginLeft = isMobile ? 0 : W * 0.065;
      marginRight = isMobile ? 0 : W * 0.045;
    }

    function buildDots() {
      dots = [];
      const ys = rowYs();
      const perRow = Math.ceil(beltWidth() / DOT_PITCH);
      for (let row = 0; row < ROW_COUNT; row++) {
        for (let i = 0; i < perRow; i++) {
          dots.push({
            phaseFrac: i / perRow,
            targetY: ys[row]!,
            // Subtle per-dot brightness texture, stable across frames
            alphaJitter: 0.84 + ((i * 7 + row * 13) % 10) * 0.016,
          });
        }
      }
    }

    function scaleX(designPx: number) {
      // Figma band starts at x=94 in a 1440 frame
      return designPx * (W / 1440);
    }

    function scatterCount() {
      return isMobile ? SCATTER_COUNT_MOBILE : SCATTER_COUNT;
    }

    function buildScatterDots() {
      scatterDots = [];
      const count = scatterCount();
      const order = shuffledIndices(count);
      for (let i = 0; i < count; i++) {
        scatterDots.push({
          cluster: i % SCATTER_CLUSTERS.length,
          startX: 0,
          startY: 0,
          landX: 0,
          landingY: 0,
          phase0: (order[i]! / count) * SCATTER_PERIOD_MS,
          entryTime: null,
          period: SCATTER_PERIOD_MS,
          rgb:
            Math.random() < SCATTER_PURPLE_CHANCE ? CIPHER_RGB : ELECTRIC_RGB,
          falling: true,
          x: 0,
          y: 0,
          alpha: 0,
        });
      }
    }

    function rollScatterCycle(d: ScatterDot, ys: number[]) {
      const cluster = SCATTER_CLUSTERS[d.cluster]!;
      const sx = scaleX(cluster.spreadX);
      const yScale = isMobile ? SCATTER_Y_SCALE_MOBILE : 1;
      // Y stays in design px so the arc height matches Figma regardless of viewport width
      const sy = cluster.spreadY * yScale;
      d.startX = beamStart() + scaleX(cluster.x) + rand(-sx, sx);
      d.startY = horizonY + cluster.y * yScale + rand(-sy, sy);
      d.landingY = ys[Math.floor(Math.random() * ys.length)]!;
      // Slight inward drift — matches the rotated arcs in Figma
      d.landX = d.startX + rand(-4, 16);
      d.period = rand(SCATTER_PERIOD_MS * 0.65, SCATTER_PERIOD_MS * 1.45);
      d.rgb = Math.random() < SCATTER_PURPLE_CHANCE ? CIPHER_RGB : ELECTRIC_RGB;
    }

    function slotCount() {
      return isMobile ? 2 : 4;
    }

    function buildCalloutDom() {
      for (const slot of slots) window.clearTimeout(slot.hideTimer);
      leadersEl.replaceChildren();
      labelsEl.replaceChildren();
      slots = [];

      const count = slotCount();
      const fracs = isMobile ? [0.42, 0.68] : [0.22, 0.4, 0.6, 0.82];

      for (let i = 0; i < count; i++) {
        const leaderRoot = document.createElement('div');
        leaderRoot.className =
          'pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300';
        const labelRoot = document.createElement('div');
        labelRoot.className =
          'pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300';

        const line = document.createElement('div');
        line.className = 'absolute w-px bg-electric';
        const tip = document.createElement('div');
        tip.className = 'absolute size-[5px] rounded-full bg-electric';
        leaderRoot.append(line, tip);

        const card = document.createElement('div');
        card.className = 'absolute text-left';
        const value = document.createElement('div');
        value.className =
          'font-planar text-16 font-medium leading-120 text-electric whitespace-nowrap';
        const label = document.createElement('div');
        // Desktop: single line always (the card flips sides instead of
        // wrapping). Mobile: Figma wraps long labels within a narrow column.
        label.className = isMobile
          ? 'font-planar text-16 font-thin leading-120 text-balance text-grayscale-03 max-w-[180px]'
          : 'font-planar text-16 font-thin leading-150 text-grayscale-03 whitespace-nowrap';
        card.append(value, label);
        labelRoot.append(card);

        leadersEl.appendChild(leaderRoot);
        labelsEl.appendChild(labelRoot);

        slots.push({
          metricIndex: i,
          frac: fracs[i] ?? 0.5,
          side: i % 2 === 0 ? 'up' : 'down',
          leaderLen: rand(isMobile ? 64 : 48, isMobile ? 100 : 90),
          hideTimer: 0,
          visible: false,
          leaderRoot,
          labelRoot,
          line,
          tip,
          card,
          value,
          label,
        });
      }
    }

    function cardBounds(slot: Slot) {
      const left = Number.parseFloat(slot.card.style.left) || 0;
      const top = Number.parseFloat(slot.card.style.top) || 0;
      const width = slot.card.offsetWidth || 180;
      const height = slot.card.offsetHeight || 48;
      return {
        left,
        top,
        right: left + width,
        bottom: top + height,
        width,
        height,
      };
    }

    function cardsOverlap(a: Slot, b: Slot, pad = 16) {
      const A = cardBounds(a);
      const B = cardBounds(b);
      return !(
        A.right + pad <= B.left ||
        B.right + pad <= A.left ||
        A.bottom + pad <= B.top ||
        B.bottom + pad <= A.top
      );
    }

    // Keep labels clear of the left intake cloud / charcoal fade.
    function intakeSafeLeft() {
      const fadeWidth = isMobile ? W * 0.38 : Math.min(314, W * 0.24);
      return Math.max(8, fadeWidth * 0.85);
    }

    function applyCardAlign(
      slot: Slot,
      x: number,
      cardW: number,
      align: 'left' | 'right',
    ) {
      if (align === 'right') {
        slot.card.style.left = `${Math.max(x - 11 - cardW, intakeSafeLeft())}px`;
        slot.card.style.textAlign = 'right';
      } else {
        slot.card.style.left = `${Math.min(
          x + 11,
          Math.max(intakeSafeLeft(), W - 8 - cardW),
        )}px`;
        slot.card.style.textAlign = 'left';
      }
    }

    function canAlignRight(x: number, cardW: number) {
      // Right-align only when the whole card stays clear of the intake cloud.
      return x - 11 - cardW >= intakeSafeLeft();
    }

    function positionSlot(slot: Slot, preferAlign?: 'left' | 'right') {
      const metric = metricsRef.current[slot.metricIndex];
      if (!metric) return;

      slot.value.textContent = metric.number;
      slot.label.textContent = metric.label;

      const x = beamStart() + slot.frac * beamWidth();
      // Outer edges of the drawn band (row centers ± dot radius)
      const bandTop = horizonY - bandHalf - DOT_R;
      const bandBottom = horizonY + bandHalf + DOT_R;

      // Measure the card first so wrapped mobile labels can stretch the
      // leader far enough that the whole block clears the band.
      slot.card.style.left = '0px';
      slot.card.style.right = 'auto';
      slot.card.style.textAlign = 'left';
      const cardW = slot.card.offsetWidth || 180;
      const cardH = slot.card.offsetHeight || 48;
      const gap = 8;

      let tipY: number;
      if (slot.side === 'up') {
        // Tip sits on the value row; leader must be tall enough that the
        // full card (value + wrapped label) clears the band top.
        const minLeader = cardH - 12 + gap;
        const leader = Math.max(slot.leaderLen, minLeader);
        tipY = bandTop - leader;
        tipY = Math.max(tipY, 12);
        slot.line.style.top = `${tipY}px`;
        slot.line.style.height = `${Math.max(bandTop - tipY, 0)}px`;
      } else {
        const minLeader = cardH - 12 + gap;
        const leader = Math.max(slot.leaderLen, minLeader);
        tipY = bandBottom + leader;
        tipY = Math.min(tipY, H - 12);
        slot.line.style.top = `${bandBottom}px`;
        slot.line.style.height = `${Math.max(tipY - bandBottom, 0)}px`;
      }
      slot.line.style.left = `${x}px`;
      slot.tip.style.left = `${x - 2}px`;
      slot.tip.style.top = `${tipY - 2}px`;

      let top = tipY - 12;
      if (slot.side === 'up') {
        top = Math.min(top, bandTop - cardH - gap);
      } else {
        top = Math.max(top, bandBottom + gap);
      }
      top = clamp(top, 4, Math.max(4, H - cardH - 4));
      slot.card.style.top = `${top}px`;

      // Prefer text to the right of the tip so labels stay out of the left
      // intake cloud. Only flip left when the card would clip the right edge
      // and that flip still clears the intake zone.
      const canLeft = x + 11 + cardW <= W - 8;
      const canRight = canAlignRight(x, cardW);
      let align: 'left' | 'right' = preferAlign ?? 'left';
      if (align === 'left' && !canLeft && canRight) align = 'right';
      if (align === 'right' && !canRight) align = 'left';
      applyCardAlign(slot, x, cardW, align);
    }

    function resolveOverlaps(slot: Slot) {
      for (const other of slots) {
        if (other === slot || !other.visible || other.side !== slot.side) {
          continue;
        }
        if (!cardsOverlap(slot, other)) continue;

        // Try flipping each card away from the other tip before giving up.
        // positionSlot will refuse a right-align that lands in the intake cloud.
        const slotX = beamStart() + slot.frac * beamWidth();
        const otherX = beamStart() + other.frac * beamWidth();
        const slotAway: 'left' | 'right' = slotX <= otherX ? 'right' : 'left';
        const otherAway: 'left' | 'right' = otherX < slotX ? 'right' : 'left';

        positionSlot(slot, slotAway);
        positionSlot(other, otherAway);

        if (!cardsOverlap(slot, other)) continue;

        // Still colliding — drop the older neighbour.
        window.clearTimeout(other.hideTimer);
        hideSlot(other);
      }
    }

    function hideSlot(slot: Slot) {
      slot.visible = false;
      for (const el of [slot.leaderRoot, slot.labelRoot]) {
        el.classList.remove('opacity-100');
        el.classList.add('opacity-0');
      }
    }

    function showSlot(slot: Slot, metricIndex: number) {
      const list = metricsRef.current;
      if (!list.length) return;

      slot.metricIndex = metricIndex % list.length;
      slot.leaderLen = rand(isMobile ? 64 : 48, isMobile ? 100 : 90);
      positionSlot(slot);
      resolveOverlaps(slot);
      slot.visible = true;
      for (const el of [slot.leaderRoot, slot.labelRoot]) {
        el.classList.remove('opacity-0');
        el.classList.add('opacity-100');
      }
      // Re-measure after paint — wrapped + text-balance height can settle
      // a frame later, and we need that final height for band clearance.
      window.requestAnimationFrame(() => {
        if (destroyed || !slot.visible) return;
        positionSlot(slot);
        resolveOverlaps(slot);
      });
      window.clearTimeout(slot.hideTimer);
      const holdMs = isMobile ? CALLOUT_HOLD_MS_MOBILE : CALLOUT_HOLD_MS;
      slot.hideTimer = window.setTimeout(() => {
        if (destroyed) return;
        hideSlot(slot);
      }, holdMs);
    }

    function advanceMetric() {
      const list = metricsRef.current;
      if (!list.length) return 0;
      const index = nextMetricCursor % list.length;
      nextMetricCursor = (nextMetricCursor + 1) % list.length;
      return index;
    }

    // Prefer a slot that isn't currently on screen so rapid clicks don't
    // teleport a visible callout (which reads as broken layering).
    function pickSlot() {
      for (let i = 0; i < slots.length; i++) {
        const candidate = slots[(slotCursor + i) % slots.length]!;
        if (!candidate.visible) {
          slotCursor = (slotCursor + i + 1) % slots.length;
          return candidate;
        }
      }
      const slot = slots[slotCursor % slots.length]!;
      slotCursor = (slotCursor + 1) % slots.length;
      return slot;
    }

    function handleClick(event: MouseEvent) {
      if (!slots.length) return;

      // Absorb click bursts (double clicks, impatient tapping): each burst
      // spawns exactly one callout.
      const now = performance.now();
      if (now - lastClickAt < 350) return;
      lastClickAt = now;

      const rect = stage.getBoundingClientRect();
      // Keep callouts out of the faded intake zone where they'd be unreadable
      const minFrac = isMobile ? 0.28 : 0.14;
      const frac = clamp(
        (event.clientX - rect.left - beamStart()) / beamWidth(),
        minFrac,
        0.88,
      );
      const side: 'up' | 'down' =
        event.clientY - rect.top < horizonY ? 'up' : 'down';

      const slot = pickSlot();
      slot.frac = frac;
      slot.side = side;
      showSlot(slot, advanceMetric());
    }

    function layout() {
      buildDots();
      buildScatterDots();
      buildCalloutDom();
    }

    function step(now: number) {
      if (destroyed) return;
      // Yield the main thread while offscreen or in a background tab so
      // interactions elsewhere (nav, CTAs) aren't competing with this rAF loop.
      if (!isVisible || !isPageVisible) {
        rafId = 0;
        return;
      }
      if (startTime === null) startTime = now;

      ctx.clearRect(0, 0, W, H);

      const bw = beltWidth();
      const startX = beamStart();
      const endX = beamEnd();
      const fadeZone = FADE_FRAC * beamWidth();
      const rate = (now - startTime) / BELT_MS;

      for (const d of dots) {
        const raw = rate + d.phaseFrac;
        const frac = raw - Math.floor(raw);
        const x = startX + frac * bw;
        if (x > endX) continue;
        const fadeInT = clamp((x - startX) / fadeZone, 0, 1);
        // Ease out over the last stretch instead of popping off at the edge —
        // dots read as being absorbed by the terminator line.
        const fadeOutT = clamp((endX - x) / 36, 0, 1);
        // Smoothstep from zero so dots condense gradually out of the dark
        // instead of switching on at the left edge.
        const fadeIn = fadeInT * fadeInT * (3 - 2 * fadeInT);
        const a = fadeIn * easeOutCubic(fadeOutT) * d.alphaJitter;
        if (a < 0.02) continue;
        ctx.beginPath();
        ctx.arc(x, d.targetY, DOT_R, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${ELECTRIC_RGB},${a.toFixed(2)})`;
        ctx.fill();
      }

      // Charcoal gradient over the left of the band only (Figma node 153:8259):
      // the beam eases in but the scatter dots drawn after stay at full
      // brightness above it.
      const fadeWidth = isMobile ? W * 0.38 : Math.min(314, W * 0.24);
      const fadeGrad = ctx.createLinearGradient(0, 0, fadeWidth, 0);
      fadeGrad.addColorStop(0.01, 'rgba(28,28,30,1)');
      fadeGrad.addColorStop(0.53, 'rgba(28,28,30,0.74)');
      fadeGrad.addColorStop(1, 'rgba(28,28,30,0)');
      ctx.fillStyle = fadeGrad;
      ctx.fillRect(0, horizonY - bandHalf - 8, fadeWidth, bandHalf * 2 + 16);

      // Vertical terminator line at the end of the beam. Figma spec:
      // linear-gradient(90deg, #1C1C1E 0%, #D8FB4B 50%, #1C1C1E 100%)
      // (electric at the middle of the line fading to charcoal at both ends).
      // Skipped on mobile where the band bleeds off-screen.
      if (!isMobile) {
        const lineHalf = bandHalf + 50;
        const lineGrad = ctx.createLinearGradient(
          0,
          horizonY - lineHalf,
          0,
          horizonY + lineHalf,
        );
        lineGrad.addColorStop(0, 'rgba(28,28,30,1)');
        lineGrad.addColorStop(0.5, 'rgba(216,251,75,1)');
        lineGrad.addColorStop(1, 'rgba(28,28,30,1)');
        ctx.fillStyle = lineGrad;
        ctx.fillRect(endX + 2, horizonY - lineHalf, 1.5, lineHalf * 2);
      }

      const ys = rowYs();
      for (const d of scatterDots) {
        if (d.entryTime === null) {
          d.entryTime = now - d.phase0;
          rollScatterCycle(d, ys);
        }
        let t = now - d.entryTime;
        if (t >= d.period) {
          d.entryTime = now;
          t = 0;
          rollScatterCycle(d, ys);
        }

        if (t < SCATTER_FALL_MS) {
          const frac = t / SCATTER_FALL_MS;
          // ease-in: linger in the high Figma clouds, then accelerate into the band
          const eased = easeInCubic(frac);
          d.x = lerp(d.startX, d.landX, eased);
          d.y = lerp(d.startY, d.landingY, eased);
          d.falling = true;
          d.alpha = lerp(0.55, 1, Math.min(frac * 2.2, 1));
        } else if (t < SCATTER_FALL_MS + SCATTER_SETTLE_MS) {
          // Landed: dissolve into the belt as electric so purple never tints it
          const settleFrac = (t - SCATTER_FALL_MS) / SCATTER_SETTLE_MS;
          d.x = d.landX;
          d.y = d.landingY;
          d.falling = false;
          d.alpha = lerp(1, 0, easeOutCubic(settleFrac));
        } else {
          d.alpha = 0;
        }

        if (d.alpha > 0.01) {
          const rgb = d.falling ? d.rgb : ELECTRIC_RGB;
          ctx.beginPath();
          ctx.arc(d.x, d.y, DOT_R, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${rgb},${d.alpha.toFixed(2)})`;
          ctx.fill();
        }
      }

      rafId = requestAnimationFrame(step);
    }

    function advanceCallout() {
      if (!slots.length || !metricsRef.current.length) return;
      const slot = slots[slotCursor % slots.length]!;
      slotCursor += 1;
      showSlot(slot, advanceMetric());
    }

    let autoTimer = 0;

    function pauseAutoAdvance() {
      if (!autoTimer) return;
      window.clearInterval(autoTimer);
      autoTimer = 0;
    }

    function resumeAutoAdvance() {
      if (
        destroyed ||
        autoTimer ||
        !isVisible ||
        !isPageVisible ||
        !slots.length ||
        !metricsRef.current.length
      ) {
        return;
      }
      autoTimer = window.setInterval(advanceCallout, AUTO_ADVANCE_MS);
    }

    // Wait, then reveal one at a time on a steady beat (no opening burst)
    const startTimer = window.setTimeout(() => {
      if (destroyed || !isVisible || !isPageVisible) return;
      advanceCallout();
      resumeAutoAdvance();
    }, INITIAL_CALLOUT_PAUSE_MS);

    const onResize = () => {
      resize();
      layout();
    };

    const kick = () => {
      if (destroyed || rafId || !isVisible || !isPageVisible) return;
      rafId = requestAnimationFrame(step);
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        isVisible = Boolean(entry?.isIntersecting);
        if (isVisible) {
          kick();
          resumeAutoAdvance();
        } else {
          pauseAutoAdvance();
        }
      },
      { rootMargin: '80px 0px', threshold: 0.05 },
    );
    io.observe(stage);

    const onVisibilityChange = () => {
      isPageVisible = !document.hidden;
      if (isPageVisible && isVisible) {
        kick();
        resumeAutoAdvance();
      } else {
        pauseAutoAdvance();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    resize();
    layout();
    kick();

    window.addEventListener('resize', onResize);
    stage.addEventListener('click', handleClick);

    return () => {
      destroyed = true;
      cancelAnimationFrame(rafId);
      window.clearTimeout(startTimer);
      pauseAutoAdvance();
      for (const slot of slots) window.clearTimeout(slot.hideTimer);
      window.removeEventListener('resize', onResize);
      stage.removeEventListener('click', handleClick);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      io.disconnect();
    };
  }, []);

  return (
    <div
      ref={stageRef}
      className={cn(
        'relative h-[300px] w-full cursor-pointer overflow-hidden m:h-[380px]',
        className,
      )}
      role="button"
      tabIndex={-1}
      aria-label="Policy engine activity visualization. Click to reveal the next metric."
    >
      {/* Leaders under the canvas (lines pass under dots); labels above */}
      <div ref={leadersRef} className="pointer-events-none absolute inset-0" />
      <canvas ref={canvasRef} className="absolute inset-0 z-[1] size-full" />
      <div
        ref={labelsRef}
        className="pointer-events-none absolute inset-0 z-[2]"
        aria-live="polite"
      />
    </div>
  );
};
